import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Router, Navigation, ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { Entry } from 'contentful';

import { AnalyticsService } from '@src/app/shared/services/fb-analytics.service';
import { ContentService } from '@src/app/shared/services/content.service';
import { EntryData } from '@src/app/shared/interfaces/entryData';

@Component({
  selector: 'app-blog-entry',
  templateUrl: './blog-entry.component.html',
  styleUrls: ['./blog-entry.component.scss']
})

export class BlogEntryComponent implements OnInit, OnDestroy {
	@ViewChild('entryContent') entryContent!: ElementRef;
	private router = inject(Router);

	pageTitle: string;

  id: string | null = null;
	entry: Entry<any> | null = null;
	entrySub: Subscription;
	entries: Entry<any>[];
	relatedEntries: Entry<any>[];

	relatedCount: number = 3;
	placeholderImg: string;

  constructor(
		private route: ActivatedRoute,
		protected analyticsService: AnalyticsService,
		private contentService: ContentService
	) { 
		this.getEntries().add(() =>
			this.route.params.subscribe(() => {
				const idParam = this.route.snapshot.paramMap.get('id');

				if (
					!this.entries.flatMap(e => [e.fields?.slug, e.sys.id]).filter(e => e).includes(idParam)
				) {
					this.router.navigate(['/blog/404-not-found']);
				}

				this.id = this.contentService.slugToID(
					idParam,
					this.entries
				);

				this.entrySub = this.contentService.getEntry(this.id).subscribe(async(data) => {
					this.entry = data;
					
					try {
						await this.analyticsService.setHtmlMeta(`${this.pageTitle} | ${this.entry?.fields.title}`);
					} catch(e) {
						console.error(e);
					} finally {
						await this.analyticsService.pageView();
					}

					this.getRelated();

					setTimeout(async() => {
						await this.contentService.citeImages(this.entryContent.nativeElement);

						this.contentService.trackContentLinks(this.entryContent);

						await Array.from(
							this.entryContent.nativeElement.getElementsByTagName('cite')
						).map((e: HTMLElement) => {
							if (e.querySelector('a'))
								e.querySelector('a').target = '_blank';
						});

						Array.from(
							this.entryContent.nativeElement.getElementsByTagName('a')
						).map((e: HTMLAnchorElement) => {
							if (e.target === '_blank')
								e.rel = 'noopener noreferrer';
						});
					}, 0)
				});
			})
		)
	}

	formatDate(dateStr: string): string {
		return new Intl.DateTimeFormat(
			'en-US',
			{
				day: '2-digit',
				month: 'long',
				year: 'numeric'
			}
		).format(
			new Date(dateStr)
		);
	}

	getEntries(): Subscription {
		const navigation = this.router.getCurrentNavigation();

		if (navigation?.extras.state) {
			return new Observable(this.entries = navigation.extras.state['data']).subscribe();
		}

		return this.contentService.getEntries(null, null, { 'fields.entryType': 'Blog' }, 'vipEntry').subscribe(data =>
			this.entries = data
		);
	}

	getRelated(): void {
		const filtered = this.entries.filter((e) => {
						const getTags = (entry: Entry<any>): string[] => {
							return entry.metadata?.tags.map(
								e => this.contentService.tagIDtoName(e.sys.id)
							)
						}

						return (
							e.fields?.categories?.some((e2) => 
								this.entry.fields?.categories?.includes(e2)
							) || 
							getTags(e).some((e2) =>
								getTags(this.entry).includes(e2)
							)
						)
					}),
					
					entryIndex = filtered.findIndex((e2) => e2.sys.id === this.entry.sys.id);

		this.relatedEntries = filtered.slice(entryIndex, filtered.length);
		this.relatedEntries.push(...filtered.slice(0, entryIndex));
		this.relatedEntries = this.relatedEntries.filter((e => e.sys.id !== this.entry.sys.id)).slice(0, this.relatedCount)
	}

	openEntry(e: EntryData) {
		this.router.navigate(['/blog/entries', e.entry.sys.id], { state: {data: e.entries} });
	}

	navEntry(num: number): void {
		if (this.entries.length === 1)
			return;

		this.contentService.navEntry(this.entries, this.entry, num, false);

		this.entrySub = this.contentService.entry.subscribe((d) => {
			this.entry = d;
			this.id = d.sys.id;

			this.router.navigate(
				[
					'/blog/entries', 
					this.contentService.getDataByID(this.id, 'fields.slug', this.entries) || this.id
				], 
				{ state: { data: this.entries } }
			);
		})
	}

	// Lifecycle Hooks

	async ngOnInit(): Promise<void> {
		this.pageTitle = this.analyticsService.metaInfo.blogEntries.title;
		this.placeholderImg = (await this.contentService.placeholderImg).fields.file.url;
  }

	ngOnDestroy(): void {
		this.getEntries().unsubscribe();
		this.entrySub.unsubscribe();
	}
}
